import React, { useEffect } from 'react';
import * as translator from "../../../utils/translator";
import './SplitDelivery.css';
import FastCar from '../../../icons/Fast-Car-White.svg';
import { MainTracker } from '../../../PixelTrackers/MainTracker';
import { CANNABIS_ID } from '../../../utils/consts';


type CartProductView = {
    cart_id: number;
    product_item_id: number;
    treatment_fee: number;
    item_price: number;
    is_prescription_drug: boolean;
    catalog_id: number;
    is_test_kit: boolean;
    quantity: number;
    only_prescription: boolean;
    title: string;
    quantityStr: string;
    imageSrc: string;
    subscriptionPlans: any[];
    type?: string;
    is_addon?: boolean;
};

type ExpressDeliveryState = {
    isAvailable: boolean;
    isEnabled: boolean;
    isSelected: boolean;
    deliveryPrice: number;
    deliveryPriceLoading: boolean;
    bonus: {
        type: string;
        value: number;
    } | null;
    shippingMethodToProductIDs: Record<number, number[]>; 
    showSplitDeliveryPopup: boolean;
    showDeliveryInfoPopup: boolean;
    expressPharmacyName: string | null;
    isExpressOnly: boolean;
    isNextDayEligible: boolean;
    nextDayFee: number;
    selectedTier: string | null;
  }

type DeliveryOption = 'split' | 'dhl';

type Props = {
    language: string | undefined;
    expressDeliveryState: ExpressDeliveryState;
    setExpressDeliveryState: React.Dispatch<React.SetStateAction<ExpressDeliveryState>>
    products: CartProductView[];
    onSelectDeliveryOption: (option: DeliveryOption) => void;
};

const SplitDelivery: React.FC<Props> = ({ language, products, onSelectDeliveryOption, expressDeliveryState, setExpressDeliveryState }) => {

    const expressProductItemIDs = Object.values(expressDeliveryState.shippingMethodToProductIDs)[0];
    
    useEffect(() => {
        if (expressDeliveryState.showSplitDeliveryPopup) {
            MainTracker.track('custom', 'split delivery popup');
        }
    }, [expressDeliveryState.showSplitDeliveryPopup]);

    const expressProducts = products.filter(p => expressProductItemIDs?.includes(p.product_item_id));
    const dhlOnlyProducts = products.filter(p => !expressProductItemIDs?.includes(p.product_item_id) && p.type !== CANNABIS_ID);

    return (
    <>
    {expressDeliveryState.showSplitDeliveryPopup &&
        <div className="delivery-options-overlay">
            <div className="delivery-options-container">
                <div className="delivery-options-header">
                    <div className="delivery-options-title-container">
                        <img id="fast-car-icon" className='fast-car-icon' src={FastCar} alt="Fast Car" />
                        <span>{translator.translate(language, "default", "split_delivery_popup/title")}</span>
                    </div>
                    <button 
                        className="delivery-options-close"
                        onClick={() => setExpressDeliveryState(prev => ({ ...prev, showSplitDeliveryPopup: false }))}
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>

                <div className="delivery-options-content">
                    <h3 className="delivery-options-message">
                        {translator.translate(language, "default", "split_delivery_popup/subtitle")}
                        <span className="delivery-options-highlight">{translator.translate(language, "default", "split_delivery_popup/subtitle_highlight")}</span>
                    </h3>

                    <p className="delivery-options-instruction">{translator.translate(language, "default", "split_delivery_popup/instruction")}</p>

                    <div className="delivery-options-cards">
                        <div className="delivery-option-card">
                            <div className="delivery-option-header">
                                <span className="delivery-option-title">{translator.translate(language, "default", "split_delivery_popup/option_1")}</span>
                            </div>
                            <ul className="delivery-option-details">
                                <li>
                                <b>{translator.translate(language, "default", "split_delivery_popup/with_express")}</b><br />
                                    <ul>
                                        { expressProducts.map((product) => (
                                            <li key={`express-${product.product_item_id}`}>
                                                {product.title}
                                            </li>
                                        ))}
                                    </ul>
                                </li>
                                <li>
                                <b>{translator.translate(language, "default", "split_delivery_popup/with_dhl")}</b><br />
                                    <ul>
                                        { dhlOnlyProducts.map((product) => (
                                            <li key={`dhl-${product.product_item_id}`}>
                                                {product.title}
                                            </li>
                                        ))}
                                    </ul>
                                </li>
                            </ul>
                            <button 
                                className="delivery-option-button"
                                onClick={() => {
                                    MainTracker.track('custom', 'Uber + DHL');
                                    onSelectDeliveryOption('split');
                                }}
                            >
                                {translator.translate(language, "default", "split_delivery_popup/split_button")}
                            </button>
                        </div>

                        {!expressDeliveryState.isExpressOnly && (
                        <div className="delivery-option-card"> 
                            <div className="delivery-option-header">
                                <span className="delivery-option-title">{translator.translate(language, "default", "split_delivery_popup/option_2")}</span>
                            </div>
                            <ul className="delivery-option-details">
                                <li>{translator.translate(language, "default", "split_delivery_popup/all_with_dhl")}</li>
                            </ul>
                            <button 
                                className="delivery-option-button"
                                onClick={() => {
                                    MainTracker.track('custom', 'DHL only');
                                    onSelectDeliveryOption('dhl');
                                }}
                            >
                                {translator.translate(language, "default", "split_delivery_popup/dhl_only_button")}
                            </button>
                        </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    }
    </>
    );
};

export default SplitDelivery;